import { useMemo } from 'react'
import { useAsyncCallback } from 'react-async-hook'

import type { WebApiResponse } from ':/_types/webapi'
import { useWorkspaceSWR } from '~/api/swr/useWorkspaceSWR.ts'

import type { Membership, Account, Workspace } from './types.ts'
import { apiFetch, apiFetchWorkspaceless } from '../../api/utils/apiFetch.ts'
import getSlug from '../../utils/getSlug.ts'

export function useWorkspace() {
  return useWorkspaceSWR<Account>('/api/workspace.json')
}

export function useCancelWorkspace() {
  const { mutate } = useWorkspace()

  return useAsyncCallback<WebApiResponse<[]>>(async () => {
    const response = await apiFetch(`/api/cancel.json`, {
      method: 'POST',
      body: {
        do_it: true,
      },
    })

    await mutate()

    return response
  })
}

export function useCreateWorkspace() {
  const { mutate } = useWorkspace()

  return useAsyncCallback<
    WebApiResponse<{ app: Workspace; currentUser: Account }>,
    [reason: string]
  >(async (slug: string) => {
    const response = await apiFetchWorkspaceless(`/api/create-new-workspace.json`, {
      method: 'POST',
      body: { slug: getSlug(slug) },
    })

    await mutate()

    return response
  })
}

export function useWorkspaceMemberships(): Membership[] {
  const { data } = useWorkspace()
  return useMemo(() => data?.memberships ?? [], [data?.memberships])
}

interface UpdateWorkspaceSettingsBody extends Record<string | number | symbol, unknown> {
  signatureAuth?: boolean
  email_on_assembly_errors?: boolean
  email_on_assembly_warnings?: boolean
  email_when_close_to_plan_limit?: boolean
  auto_replay_crashed_assemblies?: boolean
  save_template_in_assembly_json?: boolean
  require_template_id?: boolean
}

export function useUpdateWorkspaceSettings() {
  return useAsyncCallback<WebApiResponse<[]>, [UpdateWorkspaceSettingsBody]>((body) => {
    return apiFetch('/api/workspace/api-settings.json', {
      method: 'POST',
      body,
    })
  })
}

export function useRecordAssemblyExecution() {
  const { data } = useWorkspace()
  return useAsyncCallback(async () => {
    return apiFetchWorkspaceless(`/api/user/record-template-editor-assembly.json`, {
      method: 'POST',
      body: {
        workspace_id: data?.workspace?.id,
      },
    })
  })
}
